import React, { useState, useMemo, useEffect, useRef } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Grid,
  Chip,
  Box,
  IconButton,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  CircularProgress,
  Dialog,
  DialogContent,
} from "@mui/material";
import StoreSearch from "../../../Campaign/StoreSearch";
import moment from "moment";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import styles from "../../JobBatch.module.css";
import PdfIcon from "../../../../../shared/assets/icons/PdfIcon.png";
import ModalToUpdateBatches from "../../ModalToUpdateBatches";
import RecceReport from "../../../../RFPDashboard/RecceApproval/RecceReport";

const NotAccepted = ({
  isAllBatchesLoading,
  isNotAccepted,
  matchingJobs,
  setTotalNotAcceptedBatches,
  setAvailableBatches,
  fetchAllJobsBasedOnCampaignId,
  setIsNotAccepted,
  setAcceptedBatches,
  campaignData, // Make sure this prop is passed from parent
}) => {
  const [expandedBatch, setExpandedBatch] = useState(null);
  const [formattedBatches, setFormattedBatches] = useState([]);
  const [openModalToUpdateBatch, setOpenModalToUpdateBatch] = useState(false);
  const [detailsOfBatchSelected, setDetailsOfBatchSelected] = useState();
  const [loadingPartners, setLoadingPartners] = useState(false);
  const [partnersError, setPartnersError] = useState(null);
  const [openRecceReport, setOpenRecceReport] = useState(false);
  const [currentRecceJob, setCurrentRecceJob] = useState(null);
  const batchRefs = useRef({});
  const containerRef = useRef(null);

  // Check if Recce service is needed in this campaign
  const isRecceNeeded =
    campaignData?.scopeOfWork?.servicesNeeded?.includes("Recce");

  useEffect(() => {
    if (isNotAccepted.length > 0) {
      const updatedBatches = isNotAccepted.map((batch) => ({
        ...batch,
        jobs: batch.jobs || [],
      }));

      const batchesWithDateRanges = updatedBatches?.map((batch) => {
        const totalPayment = batch?.jobs?.reduce(
          (sum, job) => sum + job.paymentAmount,
          0,
        );

        const jobDates =
          batch?.jobs?.map((job) => ({
            start: new Date(job.jobStartingDate),
            end: new Date(job.jobCompletionDate),
          })) || [];

        const earliestStartDate =
          jobDates.length > 0
            ? new Date(Math.min(...jobDates.map((date) => date.start)))
            : new Date();
        const latestEndDate =
          jobDates.length > 0
            ? new Date(Math.max(...jobDates.map((date) => date.end)))
            : new Date();

        return {
          ...batch,
          dateRange: `${earliestStartDate.toLocaleDateString()} - ${latestEndDate.toLocaleDateString()}`,
          totalPayment,
        };
      });

      setFormattedBatches(batchesWithDateRanges);
      setTotalNotAcceptedBatches(batchesWithDateRanges.length);
    }
  }, [isNotAccepted, matchingJobs, setTotalNotAcceptedBatches]);

  const handleAccordionToggle = (batchTitle) => (event) => {
    event.stopPropagation();
    setExpandedBatch((prev) => (prev === batchTitle ? null : batchTitle));
  };

  const handleOpenRecceReport = (job) => {
    setCurrentRecceJob(job);
    setOpenRecceReport(true);
  };

  const handleCloseRecceReport = () => {
    setOpenRecceReport(false);
  };

  const handleBatchUpdate = (updatedBatch) => {
    setFormattedBatches((prev) =>
      prev.map((b) => (b._id === updatedBatch._id ? updatedBatch : b)),
    );

    if (detailsOfBatchSelected?._id === updatedBatch._id) {
      setDetailsOfBatchSelected(updatedBatch);
    }

    setIsNotAccepted((prev) =>
      prev.map((b) => (b._id === updatedBatch._id ? updatedBatch : b)),
    );

    if (updatedBatch.installationPartnerId) {
      setIsNotAccepted((prev) =>
        prev.filter((b) => b._id !== updatedBatch._id),
      );
      setAcceptedBatches((prev) => [...prev, updatedBatch]);
    }
  };

  const handleOpenModal = (batch) => {
    setLoadingPartners(true);
    setPartnersError(null);
    setDetailsOfBatchSelected(batch);

    setTimeout(() => {
      try {
        setLoadingPartners(false);
        setOpenModalToUpdateBatch(true);
      } catch (error) {
        setPartnersError("Failed to load installation partners");
        setLoadingPartners(false);
      }
    }, 500);
  };

  const searchData = useMemo(() => {
    const result = [];

    isNotAccepted.forEach((batch) => {
      batch.jobs?.forEach((job) => {
        result.push({
          id: job._id,
          storeName: job.storeName,

          batchTitle: batch.batchTitle,

          targetId: batch._id,

          subText: `Batch: ${batch.batchTitle}`,
        });
      });
    });

    return result;
  }, [isNotAccepted]);

  return (
    <Grid ref={containerRef} className={`${styles.scrollableContainer}`}>
      <div
        className="mx-3 py-2 sticky top-0 z-20 bg-white"
        
      >
        <StoreSearch
          data={searchData}
          searchKey="storeName"
          placeholder="Search Store Name"
          containerRef={containerRef}
          targetRefs={batchRefs}
          onSelect={(item) => {
            setExpandedBatch(item.batchTitle);
          }}
        />
      </div>
      {isAllBatchesLoading ? (
        <div className={styles.noJobsStyle}>
          <CircularProgress />
        </div>
      ) : formattedBatches.length === 0 ? (
        <div className={styles.noJobsStyle}>
          <Typography
            className={`${styles.noJobsBox} ${styles.noJobsText}`}
            variant="h6"
            align="center"
          >
            No batches available
          </Typography>
        </div>
      ) : (
        formattedBatches.map((batch, index) => (
          <Grid
            ref={(el) => (batchRefs.current[batch._id] = el)}
            item
            xs={12}
            md={12}
            key={index}
            my={1}
            mx={2}
          >
            <Accordion expanded={expandedBatch === batch.batchTitle}>
              <AccordionSummary
                expandIcon={
                  <IconButton onClick={handleAccordionToggle(batch.batchTitle)}>
                    <ExpandMoreIcon />
                  </IconButton>
                }
                aria-controls={`panel-${index}-content`}
                id={`panel-${index}-header`}
              >
                <div className="flex items-center justify-between p-4 rounded-lg bg-white w-full mx-auto">
                  <div className="flex">
                    <h1 className={`${styles.batchTitle}`}>
                      {batch.batchTitle}
                    </h1>
                  </div>
                  <div className={styles.chipContainer}>
                    <div>
                      <Chip
                        label={`Timeline: ${moment(
                          batch?.jobStartingDate,
                        ).format("DD MMM YY")} - ${moment(
                          batch?.jobCompletionDate,
                        ).format("DD MMM YY")}`}
                        className={styles.chip}
                      />
                    </div>
                    <Chip
                      label={<span>Batch Payment: ₹{batch?.totalPayment}</span>}
                      className={styles.chip}
                    />
                    <Chip
                      label={<span>Jobs: {batch?.jobs?.length || 0}</span>}
                      className={styles.chip}
                    />
                    <button
                      className="text-gray-400 hover:text-gray-600 text-xl rounded-full px-4 py-1 hover:bg-gray-100"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenModal(batch);
                      }}
                    >
                      ⋮
                    </button>
                  </div>
                </div>
              </AccordionSummary>

              <AccordionDetails className={styles.accordionDetails}>
                <Box className={`${styles.scrollableBox}`}>
                  <TableContainer component={Paper}>
                    <Table>
                      <TableHead>
                        <TableRow className={styles.tableHeader}>
                          <TableCell className={styles.tableCell}>
                            ADDRESS
                          </TableCell>
                          <TableCell className={styles.tableCell}>
                            STORE MANAGER
                          </TableCell>
                          <TableCell className={styles.tableCell}>
                            PRODUCT TYPE
                          </TableCell>
                          <TableCell className={styles.tableCell}>
                            DRIVER DETAILS
                          </TableCell>
                          <TableCell className={styles.tableCell}>
                            JOB PAY
                          </TableCell>
                          {/* Conditionally render RECCE REPORT column */}
                          {isRecceNeeded && (
                            <TableCell className={styles.tableCell}>
                              RECCE REPORT
                            </TableCell>
                          )}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {batch.jobs.map((row, index) => (
                          <TableRow key={index}>
                            <TableCell className={styles.tableCell}>
                              <div className={styles.sharedTextStyle}>
                                {row?.storeName}
                              </div>
                              <div className={styles.address2}>
                                {row.address}
                              </div>
                            </TableCell>
                            <TableCell className={styles.tableCell}>
                              <div className={styles.sharedTextStyle}>
                                {row.storeOwnerName}
                              </div>
                              <div className={styles.storeManagerContact}>
                                {row.storeOwnerPhoneNumber}
                              </div>
                            </TableCell>
                            <TableCell className={styles.tableCell}>
                              <div className={styles.sharedTextStyle}>
                                {row?.productId?.productType || "NA"}
                              </div>
                            </TableCell>
                            <TableCell className={styles.tableCell}>
                              <div className={styles.sharedTextStyle}>
                                {row.driverDetails?.driverName || "NA"}
                              </div>
                              <div className={styles.address2}>
                                {row.driverDetails?.driverPhoneNumber || "NA"} |{" "}
                                {row.driverDetails?.vehicleNumber || "NA"}
                              </div>
                            </TableCell>
                            <TableCell className={styles.tableCell}>
                              <div className={styles.sharedTextStyle}>
                                ₹{row.paymentAmount}
                              </div>
                            </TableCell>
                            {/* Conditionally render recce report cell */}
                            {isRecceNeeded && (
                              <TableCell className={styles.tableCell}>
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    cursor: "pointer",
                                  }}
                                  onClick={() => handleOpenRecceReport(row)}
                                >
                                  <img
                                    src={PdfIcon}
                                    alt="PDF Icon"
                                    style={{
                                      width: "20px",
                                      height: "20px",
                                      marginRight: "8px",
                                    }}
                                  />
                                  <a
                                    href="#"
                                    style={{
                                      cursor: "pointer",
                                      textDecoration: "none",
                                      color: "inherit",
                                    }}
                                  >
                                    {row.RecceReport}
                                  </a>
                                </div>
                              </TableCell>
                            )}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              </AccordionDetails>
            </Accordion>
          </Grid>
        ))
      )}

      {openModalToUpdateBatch && (
        <ModalToUpdateBatches
          openModal={openModalToUpdateBatch}
          setOpenModalToUpdateBatch={setOpenModalToUpdateBatch}
          batch={detailsOfBatchSelected}
          setAvailableBatches={setAvailableBatches}
          fetchAllJobsBasedOnCampaignId={fetchAllJobsBasedOnCampaignId}
          fetchAllBatchesBasedOnCampaignId={fetchAllJobsBasedOnCampaignId}
          setIsNotAccepted={setIsNotAccepted}
          setAcceptedBatches={setAcceptedBatches}
          onBatchUpdate={handleBatchUpdate}
          loadingPartners={loadingPartners}
          partnersError={partnersError}
          retryFetchPartners={() => handleOpenModal(detailsOfBatchSelected)}
          campaignData={campaignData}
        />
      )}
      <Dialog
        open={openRecceReport}
        onClose={handleCloseRecceReport}
        maxWidth="lg"
        fullWidth
        className="custom-dialog"
        PaperProps={{
          className: "custom-dialog-paper",
        }}
      >
        <DialogContent>
          {currentRecceJob && (
            <RecceReport
              setOpen={setOpenRecceReport}
              open={openRecceReport}
              currentApproval={currentRecceJob}
              finalSpaces={[]}
              installationCheck={true}
            />
          )}
        </DialogContent>
      </Dialog>
    </Grid>
  );
};

export default NotAccepted;
